import { ErrorListDisplay } from 'components/ErrorDisplay';
import {
  addClinician,
  clinicActionEditClinicianDetails
} from 'lib/api/firebase/apis/apis';
import { Clinician } from 'lib/documents/clinician';
import { useGetCliniciansList } from 'lib/hooks/tanstack/use-GetCliniciansList';
import { useQueryErrorNotifier } from 'lib/hooks/use-query-error-notifier';
import { useErrorList } from 'lib/utils/useErrorList';
import { useMemo } from 'react';
import { useCopyToClipboardMutation } from '../../../../../lib/hooks/tanstack/use-CopyToClipboardMutation';
import { BASE_USER_ADD_QUESTIONS } from '../../types/form-questions';

import UserTable from './user-table';
import { ACTION_COPYINVITELINK } from './configs/config-action-copyinvitelink';
import { BASE_ACTIONS } from './configs/shared-actions-baseconfig';
import { listUserResponseToTableRow } from './utils/listUserResponseToTableRow';
import { TagEditDetails } from 'features/clinician/clinic-user-management/components/tag-edit-details';
import {
  ActionState,
  ActionTagSettings
} from 'components/action-tag/action-tag-component';
import { TableRow } from './user-table-types';
import { useQueryDataErrors } from 'lib/hooks/use-query-data-errors';

export function ClinicianTable(props: { clinicId: string }) {
  const query = useGetCliniciansList(props.clinicId, false);
  const queryPendingUsers = useGetCliniciansList(props.clinicId, true);
  const errorList = useErrorList();
  const copyToClipboardMutation = useCopyToClipboardMutation();

  useQueryErrorNotifier(query, errorList, 'ConfirmedUsers');
  useQueryErrorNotifier(queryPendingUsers, errorList, 'PendingUsers');
  useQueryErrorNotifier(copyToClipboardMutation, errorList, 'CopyToClipboard');
  useQueryDataErrors({
    query: query,
    errorList,
    contextPrefix: 'ConfirmedUsers: '
  });
  useQueryDataErrors({
    query: queryPendingUsers,
    errorList,
    contextPrefix: 'PendingUsers: '
  });

  const data = useMemo(
    () => [
      ...(query.data?.pages ?? []).flatMap(_ =>
        listUserResponseToTableRow(_.data, { invitePending: false })
      ),
      ...(queryPendingUsers.data?.pages ?? []).flatMap(_ =>
        listUserResponseToTableRow(_.data, { invitePending: true })
      )
    ],
    [query.data, queryPendingUsers.data]
  );

  return (
    <div>
      <ErrorListDisplay errorList={errorList} />
      <UserTable<
        Clinician & {
          firstName: string;
          lastName: string;
          invitePending: boolean;
        }
      >
        typeUnit="Clinician"
        onAddUser={(user, clinicId) => addClinician({ ...user, clinicId })}
        data={data}
        isLoading={query.isLoading}
        refetchData={() => {
          query.refetch();
          queryPendingUsers.refetch();
        }}
        onError={(error, metadata) =>
          errorList.addError(error, `${metadata.action} - ${metadata.id}`)
        }
        clinicId={props.clinicId}
        actions={[
          {
            name: 'Edit',
            initialState: () => ActionState.NEUTRAL,
            render: _props => (
              <TagEditDetails
                {..._props}
                userAddQuestions={BASE_USER_ADD_QUESTIONS}
                initialValues={{
                  firstName: _props.record.firstName,
                  lastName: _props.record.lastName,
                  email: _props.record.email
                }}
              />
            ),
            onClick: p => {
              if (!p.raw.id) {
                throw new Error(
                  'ID is undefined. Please try reloading the page'
                );
              }

              return clinicActionEditClinicianDetails({
                uid: p.raw.id,
                firstName: p.firstName,
                lastName: p.lastName,
                email: p.email
              });
            },
            onSuccess: refetch => refetch()
          },
          ...(BASE_ACTIONS as unknown as ActionTagSettings<
            TableRow<
              Clinician & {
                invitePending: boolean;
                firstName: string;
                lastName: string;
              }
            >
          >[]),
          ACTION_COPYINVITELINK(copyToClipboardMutation.mutateAsync)
        ]}
        userAddQuestions={BASE_USER_ADD_QUESTIONS}
      />
    </div>
  );
}
